<template>
  <div class="language-switcher">
    <select v-if="showSelect" v-model="selectedLanguage" @change="onLanguageChange" class="language-select">
      <option v-for="lang in languageList" :key="lang" :value="lang">{{ showFlagInOptions ? getFlagEmoji(lang) + ' ' : '' }}{{ getLanguageName(lang) }}</option>
    </select>
  </div>
</template>

<script>
export default {
  name: 'LanguageSwitcher',
  props: {
    selected: {
      type: String,
      default: 'pt',
    },
    showFlag: {
      type: Boolean,
      default: false,
    },
    showSelect: {
      type: Boolean,
      default: true,
    },
    showFlagInOptions: {
      type: Boolean,
      default: true,
    },
    flagStyle: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedLanguage: this.selected || this.$store?.state?.user?.language || 'pt',
      selectedFlag: '',
      languageList: this.getLanguageList(),
    };
  },
  watch: {
    selected(newVal) {
      this.selectedLanguage = newVal || 'pt';
      this.updateSelectedFlag();
    },
    selectedLanguage(newVal) {
      this.updateSelectedFlag();
    },
    // Watch for changes in store language and update component
    '$store.state.user.language': {
      handler(newLanguage) {
        if (newLanguage && newLanguage !== this.selectedLanguage) {
          this.selectedLanguage = newLanguage;
          this.updateSelectedFlag();
        }
      },
      immediate: false,
    },
    // Watch for changes in multilanguagesList in store
    '$store.state.template.multilanguagesList': {
      handler(newList) {
        this.languageList = this.getLanguageList();
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    if (!this.selectedLanguage) {
      this.selectedLanguage = this.$store?.state?.user?.language || 'pt';
    }
    this.languageList = this.getLanguageList();
    this.updateSelectedFlag();
  },
  methods: {
    onLanguageChange() {
      this.updateSelectedFlag();
      const languageData = {
        code: this.selectedLanguage,
        flag: this.selectedFlag,
        name: this.getLanguageName(this.selectedLanguage),
      };
      this.$emit('change', languageData);
    },
    cycleLanguage() {
      const currentIndex = this.languageList.indexOf(this.selectedLanguage);
      const nextIndex = (currentIndex + 1) % this.languageList.length;
      this.selectedLanguage = this.languageList[nextIndex];
      this.onLanguageChange();
    },
    updateSelectedFlag() {
      this.selectedFlag = this.selectedLanguage;
      // Map language codes to flag codes
      if (this.selectedLanguage === 'en') {
        this.selectedFlag = 'us';
      } else if (this.selectedLanguage === 'pt') {
        this.selectedFlag = 'br';
      }
    },
    getLanguageList() {
      // Try to get from store, fallback to defaults
      const list = this.$store?.state?.template?.multilanguagesList;
      if (Array.isArray(list) && list.length > 0) {
        return list;
      }
      return ['pt', 'en', 'es', 'it', 'de','fr'];
    },
    getLanguageName(code) {
      const names = {
        pt: 'Português',
        en: 'English',
        es: 'Español',
        it: 'Italiano',
        de: 'Deutsch',
        fr: 'Français',
      };
      return names[code] || code;
    },
    getFlagEmoji(code) {
      const flagMap = {
        pt: '🇧🇷', // Brazil flag for Portuguese
        en: '🇺🇸', // US flag for English
        es: '🇪🇸', // Spain flag for Spanish
        it: '🇮🇹', // Italy flag for Italian
        de: '🇩🇪', // Germany flag for German
        fr: '🇫🇷', // France flag for French
      };
      return flagMap[code] || '🌐'; // Globe emoji as fallback
    },
  },
};
</script>

<style>
.language-switcher {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.flag-container {
  display: inline-flex;
  align-items: center;
}

.flag-container .flag {
  width: 20px;
  height: auto;
  border-radius: 2px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.flag-container .flag.clickable-flag {
  cursor: pointer;
}

.flag-container .flag.clickable-flag:hover {
  transform: scale(1.1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.language-select {
  background-color: transparent;
  border: none;
  color: var(--menu-color, #333);
  opacity: 0.7;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  outline: none;
  min-width: 100px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
}

.language-select:hover {
  border-color: var(--hover-color, #666);
}

.language-select option {
  background-color: var(--header-bgColor, white);
  color: var(--menu-color, #333);
  padding: 5px 8px;
  font-family: inherit;
}
</style>
